import { ColumnDef } from '@tanstack/react-table'
import MetricCell from 'app/components/table/MetricCell'

export type StaffMetricColumnOptions = {
  /** Add sortingFn: onlySortParentRows for grouped table rows */
  groupedRows?: boolean
  /** Add onClick handlers (requires onCallClick callback) */
  onCallClick?: (row: any, type: string) => void
}

/**
 * Shared staff performance metric columns used by both Scorecards and Marketing Analytics.
 * The Staff name column is NOT included — each consumer provides their own.
 */
export const staffMetricColumns = (options: StaffMetricColumnOptions = {}): ColumnDef<any, any>[] => {
  const { groupedRows, onCallClick } = options
  const extra = groupedRows ? { sortingFn: `onlySortParentRows` as any } : {}

  return [
    {
      accessorFn: (row) => row.qualifiedNPCalls,
      id: `Qualified NP Calls`,
      header: `Qualified NP Calls`,
      cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
      meta: {
        ...(onCallClick ? { onClick: (row) => onCallClick(row, `qualified-np`) } : {}),
        group: `Calls`,
        defaultVisible: true,
      },
      ...extra,
      footer: ({ table }) => (
        <MetricCell
          value={table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + (row.original?.qualifiedNPCalls ?? 0), 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorFn: (row) => row.scheduledNPCalls,
      id: `Scheduled NP Calls`,
      header: `Scheduled NP Calls`,
      cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
      meta: {
        ...(onCallClick ? { onClick: (row) => onCallClick(row, `scheduled-np`) } : {}),
        group: `Calls`,
        defaultVisible: true,
      },
      ...extra,
      footer: ({ table }) => (
        <MetricCell
          value={table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + (row.original?.scheduledNPCalls ?? 0), 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorFn: (row) => row.unscheduledNPCalls,
      id: `Unscheduled NP Calls`,
      header: `Unscheduled NP Calls`,
      cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
      meta: {
        ...(onCallClick ? { onClick: (row) => onCallClick(row, `unscheduled-np`) } : {}),
        group: `Calls`,
        defaultVisible: true,
      },
      ...extra,
      footer: ({ table }) => (
        <MetricCell
          value={table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + (row.original?.unscheduledNPCalls ?? 0), 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorFn: (row) => row.averageDuration,
      id: `Average Call Duration`,
      header: `Average Call Duration`,
      cell: (props) => <MetricCell value={props.getValue()} type="time" />,
      meta: { group: `Calls`, defaultVisible: true },
      ...extra,
      footer: ({ table }) => {
        const rows = table.getFilteredRowModel().rows
        const totalQualified = rows.reduce((s, r) => s + (r.original?.qualifiedNPCalls ?? 0), 0)
        const weightedSum = rows.reduce(
          (s, r) => s + (r.original?.averageDuration ?? 0) * (r.original?.qualifiedNPCalls ?? 0),
          0,
        )
        return <MetricCell value={totalQualified ? weightedSum / totalQualified : 0} type="time" />
      },
    },
    {
      accessorFn: (row) => row.speedToLead,
      id: `Speed to Lead`,
      header: `Speed to Lead`,
      cell: (props) => <MetricCell value={props.getValue()} type="days" />,
      meta: { group: `Calls`, defaultVisible: true },
      ...extra,
      footer: ({ table }) => {
        const rows = table.getFilteredRowModel().rows
        const totalQualified = rows.reduce((s, r) => s + (r.original?.qualifiedNPCalls ?? 0), 0)
        const weightedSum = rows.reduce(
          (s, r) => s + (r.original?.speedToLead ?? 0) * (r.original?.qualifiedNPCalls ?? 0),
          0,
        )
        return <MetricCell value={totalQualified ? weightedSum / totalQualified : 0} type="days" />
      },
    },
    {
      accessorFn: (row) => row.NPCallScheduleRate,
      id: `NP Call Schedule Rate`,
      header: `NP Call Schedule Rate`,
      cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
      meta: { group: `Calls`, defaultVisible: true },
      ...extra,
      footer: ({ table }) => {
        const rows = table.getFilteredRowModel().rows
        const totalScheduled = rows.reduce((s, r) => s + (r.original?.scheduledNPCalls ?? 0), 0)
        const totalQualified = rows.reduce((s, r) => s + (r.original?.qualifiedNPCalls ?? 0), 0)
        return <MetricCell value={totalQualified ? (totalScheduled / totalQualified) * 100 : 0} type="percentage" />
      },
    },
  ]
}
